// services/shippingPerf.ts — Ambang performa kurir dari SETTING (bukan hardcode).
// Sumber: website_settings.shippingPerf { minHigh, minMid } — diubah di
// Pengaturan Website → Ongkir & Cek Resi (insiden SiCepat #88dcc01c: user minta
// tidak ada lagi angka 80/70 tertanam di kode). Default = perilaku lama.
import { useContext } from 'react';
import { SettingsContext } from '../contexts/SettingsContext';

export interface PerfThresholds { minHigh: number; minMid: number; }

export const DEFAULT_PERF_THRESHOLDS: PerfThresholds = { minHigh: 80, minMid: 70 };

const clampPct = (v: unknown, fb: number): number => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.min(100, Math.max(0, Math.round(n)));
};

// Baca dari objek website_settings apa adanya (aman untuk null/parsial).
export function resolvePerfThresholds(ws?: any): PerfThresholds {
  const raw = ws?.shippingPerf || {};
  const minHigh = clampPct(raw.minHigh ?? raw.min_high ?? DEFAULT_PERF_THRESHOLDS.minHigh, DEFAULT_PERF_THRESHOLDS.minHigh);
  let minMid = clampPct(raw.minMid ?? raw.mid ?? DEFAULT_PERF_THRESHOLDS.minMid, DEFAULT_PERF_THRESHOLDS.minMid);
  if (minMid > minHigh) minMid = minHigh;
  return { minHigh, minMid };
}

// Hook untuk komponen: ikut setting live, fallback default bila belum termuat.
export function usePerfThresholds(): PerfThresholds {
  let ws: any = null;
  try {
    ws = useContext(SettingsContext)?.websiteSettings || null;
  } catch { ws = null; }
  return resolvePerfThresholds(ws);
}

let cachedAsync: PerfThresholds | null = null;
let cachedAsyncAt = 0;

// Getter untuk konteks non-hook (service async): baca setting live via Supabase,
// cache 5 menit, gagal → default. Jangan hardcode angka di caller.
export async function getPerfThresholds(): Promise<PerfThresholds> {
  const now = Date.now();
  if (cachedAsync && now - cachedAsyncAt < 5 * 60 * 1000) return cachedAsync;
  try {
    const { supabase } = await import('../supabase');
    const { data } = await supabase.from('settings').select('website_settings').eq('id', 'website').maybeSingle();
    cachedAsync = resolvePerfThresholds((data as any)?.website_settings);
  } catch {
    cachedAsync = { ...DEFAULT_PERF_THRESHOLDS };
  }
  cachedAsyncAt = now;
  return cachedAsync as PerfThresholds;
}
